import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { type MouseEvent, type ReactNode, useState } from "react";

import { BackLink } from "./BackLink";

/** One entry of the overflow menu. */
export interface DetailHeaderAction {
  /** Stable identity for keys and the default test id (`<menuTestId>-<id>`). */
  id: string;
  /** Resolved text — the design system has no catalogue of its own. */
  label: string;
  icon?: ReactNode;
  /**
   * Overrides the derived `<menuTestId>-<id>`. A screen whose menu button and menu
   * entry belong to different `area.element` names — the notification centre's
   * `notifications.menu` and `notifications.mark-read` — cannot express both through
   * one prefix, and naming the entry is cheaper than naming the button wrongly.
   */
  testId?: string;
  onSelect: () => void | Promise<void>;
}

/**
 * The top row of a detail screen: the way back on the left and, when the screen has
 * secondary actions, a three-dot menu on the right.
 *
 * Every detail screen — article, event, highlight, a profile setting — opens with the
 * same row, so the row lives here rather than each screen laying out its own back
 * link and, one day, its own menu. Actions are data: a screen declares what it can do
 * and the header owns how that is presented, which keeps the menu's behaviour (anchor,
 * dismissal, keyboard) in one place.
 *
 * With no actions the header is just the back link, so a screen that has nothing to
 * offer pays nothing for the shared component.
 */
export function DetailHeader({
  backTo,
  backLabel,
  backTestId,
  actions = [],
  menuLabel,
  menuTestId = "detail.menu",
}: {
  backTo: string;
  backLabel: string;
  /** Passed straight to `BackLink`, so existing specs keep their targets. */
  backTestId?: string;
  actions?: DetailHeaderAction[];
  /** Accessible name of the three-dot button. Required when there are actions. */
  menuLabel?: string;
  menuTestId?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = (event: MouseEvent<HTMLElement>) =>
    setAnchor(event.currentTarget);
  const close = () => setAnchor(null);

  const select = (action: DetailHeaderAction) => {
    close();
    void action.onSelect();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <BackLink to={backTo} label={backLabel} testId={backTestId} />

      {actions.length > 0 ? (
        <>
          <IconButton
            onClick={open}
            aria-label={menuLabel}
            aria-haspopup="menu"
            aria-expanded={anchor !== null}
            aria-controls={anchor ? `${menuTestId}-menu` : undefined}
            data-testid={menuTestId}
            sx={{
              // Mirrors the back link, which pulls its chevron out to the gutter: the
              // dots, not the tap target around them, line up with the right edge.
              marginInlineEnd: "-8px",
              color: "text.secondary",
            }}
          >
            <MoreVertIcon />
          </IconButton>

          <Menu
            id={`${menuTestId}-menu`}
            anchorEl={anchor}
            open={anchor !== null}
            onClose={close}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {actions.map((action) => (
              <MenuItem
                key={action.id}
                onClick={() => select(action)}
                data-testid={action.testId ?? `${menuTestId}-${action.id}`}
              >
                {action.icon ? (
                  <ListItemIcon>{action.icon}</ListItemIcon>
                ) : null}
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
    </Box>
  );
}
